'use client';

import { useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { BottomNavIcon, type BottomNavIconName } from '@/lib/components/BottomNavIcon';
import { MobileSearchPanel } from '@/lib/components/SearchPopoverV2';
import { FEATURE_FLAGS } from '@/lib/config/features';
import { useAuthSession } from '@/lib/hooks/useAuth';
import { Link, usePathname, useRouter } from '@/lib/routing';
import { Dialog, DialogContent, DialogTitle } from '@/lib/ui-kit/dialog';
import { Text } from '@/lib/ui-kit/text';
import { cn } from '@/lib/ui-kit/utils';
import { matchRouteUrl } from '@/lib/utils/matchRouteUrl';

type BottomItem = {
  labelKey: string;
  href: string; // actual link target
  activeHref?: string; // optional href to use for active matching
  icon: 'Stock' | 'News2' | 'Coins' | 'Search';
  exact?: boolean;
  compareSearchParams?: boolean;
  requiresAuth?: boolean;
  /** When the item lives in a different zone, render as plain <a> (multi-zone). */
  zone?: 'web' | 'news' | 'trade';
};

const ITEMS: BottomItem[] = [
  { labelKey: 'markets', href: '/', icon: 'Stock', exact: true, zone: 'web' },
  { labelKey: 'search', href: '/search', icon: 'Search', zone: 'web' },
  // The trade zone, pre-production only for now — mirrors HeaderV2Container.
  ...(FEATURE_FLAGS.ENABLE_TRADE_ZONE
    ? [{ labelKey: 'trade', href: '/trade', icon: 'Stock', zone: 'trade' } as BottomItem]
    : []),
  {
    labelKey: 'wallet',
    href: '/account?tab=wallet',
    activeHref: '/account',
    icon: 'Coins',
    requiresAuth: true,
    zone: 'web',
  },
  { labelKey: 'news', href: '/news', icon: 'News2', zone: 'news' },
];

/**
 * Fixed bottom navigation for mobile/tablet screens.
 * Shows on widths ≤ 768px (Tailwind sm breakpoint) and stays attached to viewport bottom.
 * We mount it in app/trade layouts; auth layout excludes it.
 */
export function BottomNavBar({ className }: { className?: string }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = useLocale();
  const { isAuthenticated } = useAuthSession();
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const t = useTranslations('nav');
  // Cross-zone targets must be plain <a> in multi-zone mode.
  const currentZone: 'web' | 'news' | 'trade' = pathname?.includes('/news')
    ? 'news'
    : pathname?.includes('/trade')
      ? 'trade'
      : 'web';

  const isActive = (item: BottomItem) => {
    const target = item.activeHref ?? item.href;
    return matchRouteUrl({
      currentPathname: pathname,
      currentSearchParams: searchParams,
      linkHref: target,
      exact: item.exact ?? false,
      compareSearchParams: item.compareSearchParams ?? false,
    });
  };

  useEffect(() => {
    const handleOpenSearch = () => {
      setIsSearchDialogOpen(true);
    };

    window.addEventListener('open-mobile-search', handleOpenSearch);

    return () => {
      window.removeEventListener('open-mobile-search', handleOpenSearch);
    };
  }, []);

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 border-t border-v2-border bg-background text-v2-text-primary',
        'min-[1024px]:hidden',
        className,
      )}
    >
      <ul className="mx-auto flex max-w-[640px] items-center justify-between px-8 py-3">
        {ITEMS.map((item) => {
          const active = isActive(item);

          // Handle Search button separately - open modal instead of navigation
          if (item.icon === 'Search') {
            return (
              <li key={item.href} className="list-none">
                <button
                  onClick={() => setIsSearchDialogOpen(true)}
                  onMouseEnter={() => setHoveredKey(item.labelKey)}
                  onMouseLeave={() =>
                    setHoveredKey((prev) => (prev === item.labelKey ? null : prev))
                  }
                  className={cn(
                    `group flex w-full cursor-pointer flex-col items-center gap-1 border-none bg-transparent p-0
                    no-underline`,
                  )}
                  aria-label={t(item.labelKey as Parameters<typeof t>[0])}
                  type="button"
                >
                  <BottomNavIcon
                    name={item.labelKey as BottomNavIconName}
                    active={active}
                    hovered={hoveredKey === item.labelKey}
                    size={24}
                    className={cn(
                      'transition-colors',
                      active
                        ? 'text-v2-text-primary'
                        : 'text-v2-text-secondary group-hover:text-v2-text-primary',
                    )}
                  />
                  <Text
                    variant="body-xs"
                    className={cn(
                      'transition-colors',
                      active
                        ? 'text-v2-text-primary'
                        : 'text-v2-text-secondary group-hover:text-v2-text-primary',
                    )}
                  >
                    {t(item.labelKey as Parameters<typeof t>[0])}
                  </Text>
                </button>
              </li>
            );
          }

          const isCrossZone = item.zone !== undefined && item.zone !== currentZone;
          // For cross-zone targets, render <a> (hard nav) with locale prefix.
          const Anchor = isCrossZone ? 'a' : Link;
          const href = isCrossZone
            ? `/${locale}${item.href.startsWith('/') ? item.href : `/${item.href}`}`
            : item.href;

          const content = (
            <Anchor
              href={href}
              className={cn('group flex flex-col items-center gap-1 no-underline')}
              aria-label={t(item.labelKey as Parameters<typeof t>[0])}
              onMouseEnter={() => setHoveredKey(item.labelKey)}
              onMouseLeave={() => setHoveredKey((prev) => (prev === item.labelKey ? null : prev))}
              onClick={(e) => {
                if (item.requiresAuth && !isAuthenticated) {
                  localStorage.setItem('pendingForAuthUrl', item.href);
                  e.preventDefault();
                  router.push('/auth/login');
                }
              }}
            >
              <BottomNavIcon
                name={item.labelKey as BottomNavIconName}
                active={active}
                hovered={hoveredKey === item.labelKey}
                size={24}
                className={cn(
                  'transition-colors',
                  active
                    ? 'text-v2-text-primary'
                    : 'text-v2-text-secondary group-hover:text-v2-text-primary',
                )}
              />
              <Text
                variant="body-xs"
                className={cn(
                  'transition-colors',
                  active
                    ? 'text-v2-text-primary'
                    : 'text-v2-text-secondary group-hover:text-v2-text-primary',
                )}
              >
                {t(item.labelKey as Parameters<typeof t>[0])}
              </Text>
            </Anchor>
          );

          return (
            <li key={item.href} className="list-none">
              {content}
            </li>
          );
        })}
      </ul>

      <Dialog open={isSearchDialogOpen} onOpenChange={setIsSearchDialogOpen}>
        <DialogContent
          className="inset-x-0 bottom-[60px] top-0 h-[calc(100vh-60px)] w-screen max-w-none translate-x-0 translate-y-0
            overflow-y-auto rounded-none border-0 bg-v2-surface-primary px-5 pb-5 pt-[50px]"
        >
          <DialogTitle className="sr-only">{t('search')}</DialogTitle>
          <MobileSearchPanel onClose={() => setIsSearchDialogOpen(false)} />
        </DialogContent>
      </Dialog>
    </nav>
  );
}

export default BottomNavBar;
